import { Heading } from "@/components/ui/heading";
import { IconButton } from "@/components/ui/icon-button";
import * as Menu from "@/components/ui/menu";
import { Text } from "@/components/ui/text";
import { appDialogConfirm } from "@components";
import { AppTooltip } from "@components/AppTooltip";
import { useNavigate } from "@solidjs/router";
import { useAuth, userId } from "@state/auth";
import type { GoReadPostRow } from "@state/goRead/api";
import { useGoRead } from "@state/goRead/state";
import { Edit, EllipsisVertical, MessageSquare, Trash2 } from "lucide-solid";
import { Show } from "solid-js";
import { Portal } from "solid-js/web";
import { useService } from "solid-services";
import { css } from "styled-system/css";
import { Box, Flex, Grid } from "styled-system/jsx";
import AuthorFooter from "@features/communityContent/AuthorFooter";
import { TOPICS } from "@features/communityContent/topics";
import Chip from "../../Setups/Chip";
import { excerpt, readMinutes } from "../utils";
import Cover from "./Cover";
import { useState } from "./state";

export default function Row(props: { post: GoReadPostRow }) {
  const navigate = useNavigate();
  const { setEditPost } = useService(useState)();
  const { deletePost } = useService(useGoRead)();
  const { isAdmin } = useService(useAuth)();

  const isOwner = () => isAdmin() || props.post.profile_id === userId();
  const topic = () => TOPICS[props.post.topic];

  return (
    <Grid
      position='relative'
      gridTemplateColumns={{ base: '172px 1fr auto', smDown: '1fr' }}
      gap={{ base: '18px', smDown: '0' }}
      bg='editorialPanel'
      borderRadius='14px'
      overflow='hidden'
      cursor='pointer'
      onClick={() => navigate(`/go-read/${props.post.id}`)}
      class={css({
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'var(--colors-editorial-hair)',
        transition: 'transform 0.2s, border-color 0.2s, box-shadow 0.22s',
        _hover: {
          transform: 'translateY(-2px)',
          borderColor: 'var(--colors-editorial-hair-strong)',
          boxShadow: '0 1px 2px rgba(20,22,27,.05), 0 18px 40px -24px rgba(20,22,27,.28)',
        },
      })}
    >
      <Show when={isOwner()}>
        <Box position='absolute' top='9px' right='9px' zIndex={3} onClick={(e) => e.stopPropagation()}>
          <Menu.Root
            onSelect={(details) => {
              if (details.value === 'edit') setEditPost(props.post);
              if (details.value === 'delete') {
                appDialogConfirm(
                  () => deletePost(props.post.id),
                  'Are you sure you want to delete this post?'
                )();
              }
            }}
          >
            <Menu.Trigger
              asChild={(triggerProps) => (
                <IconButton variant='outline' size='2xs' bg='editorialPanel' {...triggerProps()}>
                  <EllipsisVertical size={14} />
                </IconButton>
              )}
            />
            <Portal>
              <Menu.Positioner>
                <Menu.Content>
                  <Menu.Item minH={8} value="edit">
                    <Edit size={14} />
                    <Text textStyle="xs">Edit</Text>
                  </Menu.Item>
                  <Menu.Item minH={8} value="delete" color="error.10">
                    <Trash2 size={14} />
                    <Text textStyle="xs">Delete</Text>
                  </Menu.Item>
                </Menu.Content>
              </Menu.Positioner>
            </Portal>
          </Menu.Root>
        </Box>
      </Show>

      <Box
        position='relative'
        overflow='hidden'
        bg='#0d1117'
        minHeight={{ base: 'auto', smDown: '140px' }}
        aspectRatio={{ smDown: '16/9' }}
      >
        <Cover seed={props.post.id} color={topic().color} />
        <Box position='absolute' top={0} left={0} right={0} height='3px' style={{ background: topic().color }} />
      </Box>

      <Grid
        gap='8px'
        minWidth={0}
        py='15px'
        pr={{ base: '0', smDown: '16px' }}
        pl={{ base: '0', smDown: '16px' }}
      >
        <Flex alignItems='center' gap='9px' flexWrap='wrap'>
          <Text fontSize='11.5px' color='editorialInk3'>{readMinutes(props.post.content)} min read</Text>
          <Chip color={topic().color}>{topic().label}</Chip>
        </Flex>

        <Heading truncate fontSize='16.5px' fontWeight='700' letterSpacing='-.015em' lineHeight='1.32' color='editorialInk'>
          {props.post.title}
        </Heading>

        <Text fontSize='13px' color='editorialInk2' lineHeight='1.5' lineClamp={2}>
          {excerpt(props.post.content)}
        </Text>

        <Box marginTop='auto'>
          <AuthorFooter
            username={props.post.author_username}
            id={props.post.profile_id}
            rank={props.post.author_role}
            date={props.post.created_at}
          />
        </Box>
      </Grid>

      <Flex
        p={{ base: '15px 16px', smDown: '10px 16px 14px' }}
        gap='8px'
        alignItems='center'
        justifyContent={{ base: 'center', smDown: 'flex-start' }}
        onClick={(e) => e.stopPropagation()}
      >
        <AppTooltip content={`${props.post.comment_count} comment${props.post.comment_count === 1 ? '' : 's'}`}>
          <Flex
            alignItems='center'
            gap='5px'
            h='24px'
            px='9px'
            borderRadius='7px'
            bg='editorialChip'
            color='editorialInk2'
            fontSize='11px'
            fontWeight='600'
            class={css({
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: 'var(--colors-editorial-hair-strong)',
            })}
          >
            <MessageSquare size={13} />
            {props.post.comment_count}
          </Flex>
        </AppTooltip>
      </Flex>
    </Grid>
  );
}
